import React from 'react';
import { Button } from '@mui/material';

import { getPermissionDeniedAction } from '../utils/permissionErrors';

/**
 * RequestAccessButton - "Request access" affordance for an inline error banner.
 *
 * Toasts get this button automatically via `notifyPermissionError`. Screens
 * that render a permission denial as an inline `<Alert>` instead (a form that
 * keeps the message next to the failed action) can drop this into the Alert's
 * `action` slot to get the same behavior: opens the in-app request dialog when
 * `PermissionRequestProvider` is mounted, otherwise links to the Permissions
 * app.
 *
 * Renders nothing when *error* isn't a permission denial, so it's safe to
 * include unconditionally in a banner that shows other failures too.
 *
 * @param {Object} props
 * @param {Object} props.error - The caught error (an axios-style 403)
 * @param {string} [props.size='small'] - MUI Button size
 * @param {string} [props.color='inherit'] - MUI Button color
 *
 * @example
 * {error && (
 *   <Alert severity="error" action={<RequestAccessButton error={error} />}>
 *     {message}
 *   </Alert>
 * )}
 */
export const RequestAccessButton = ({ error, size = 'small', color = 'inherit' }) => {
  // getPermissionDeniedAction returns null exactly when this is not a denial,
  // so one guard covers both.
  const action = getPermissionDeniedAction(error);
  if (!action) return null;

  // A real anchor carrying both: the handler lookup happens inside onClick, so
  // whether this opens the in-app dialog or follows the link is decided when
  // the user clicks — not now, which may be before the provider's registration
  // effect has run. See getPermissionDeniedAction.
  return (
    <Button
      size={size}
      color={color}
      component="a"
      href={action.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );
};

export default RequestAccessButton;
